import { useState, useCallback, useEffect } from 'react';
import { isCodeClientExists } from '../../../server/services/client-service';

const CODE_CACHE_DURATION_MS = 15 * 60 * 1000;

export function useCodeValidationGate({ codeSessionStorageKey }) {
  const [codeClient, setCodeClient] = useState('');
  const [isCodeValidated, setIsCodeValidated] = useState(false);
  const [codeValidationError, setCodeValidationError] = useState('');
  const [isCheckingCode, setIsCheckingCode] = useState(false);

  useEffect(() => {
    const rawCachedCodeClient = sessionStorage.getItem(codeSessionStorageKey);
    if (!rawCachedCodeClient) {
      return;
    }

    let cachedCodeClient = null;
    let expirationTimestamp = null;

    try {
      const parsedCacheValue = JSON.parse(rawCachedCodeClient);
      if (parsedCacheValue && typeof parsedCacheValue === 'object') {
        cachedCodeClient = (parsedCacheValue.codeClient || '').trim();
        expirationTimestamp = Number(parsedCacheValue.expiresAt) || null;
      }
    } catch (error) {
      cachedCodeClient = rawCachedCodeClient.trim();
    }

    if (!cachedCodeClient) {
      sessionStorage.removeItem(codeSessionStorageKey);
      return;
    }

    if (expirationTimestamp && Date.now() > expirationTimestamp) {
      sessionStorage.removeItem(codeSessionStorageKey);
      return;
    }

    if (!expirationTimestamp && rawCachedCodeClient === cachedCodeClient) {
      const migratedCacheValue = {
        codeClient: cachedCodeClient,
        expiresAt: Date.now() + CODE_CACHE_DURATION_MS
      };
      sessionStorage.setItem(codeSessionStorageKey, JSON.stringify(migratedCacheValue));
    }

    if (cachedCodeClient) {
      setCodeClient(cachedCodeClient);
      setIsCodeValidated(true);
      setCodeValidationError('');
    }
  }, [codeSessionStorageKey]);

  const validateCodeClientValue = useCallback(async (rawCodeClient) => {
    const trimmedCode = (rawCodeClient || '').trim();
    if (!trimmedCode) {
      return false;
    }
    const response = await isCodeClientExists(trimmedCode);
    return response?.data === true || response === true;
  }, []);

  const handleValidateCodeClient = useCallback(async () => {
    const trimmedCode = (codeClient || '').trim();
    if (!trimmedCode) {
      setCodeValidationError('Veuillez entrer un code client valide.');
      return;
    }
    setIsCheckingCode(true);
    setCodeValidationError('');
    try {
      const isValid = await validateCodeClientValue(trimmedCode);
      if (!isValid) {
        setIsCodeValidated(false);
        setCodeValidationError('Le code client est invalide.');
        sessionStorage.removeItem(codeSessionStorageKey);
        return;
      }
      setIsCodeValidated(true);
      setCodeValidationError('');
      const cacheValue = {
        codeClient: trimmedCode,
        expiresAt: Date.now() + CODE_CACHE_DURATION_MS
      };
      sessionStorage.setItem(codeSessionStorageKey, JSON.stringify(cacheValue));
    } catch (error) {
      setIsCodeValidated(false);
      setCodeValidationError('Validation impossible. Veuillez réessayer.');
    } finally {
      setIsCheckingCode(false);
    }
  }, [codeClient, codeSessionStorageKey, validateCodeClientValue]);

  const handleCodeFormSubmit = useCallback((e) => {
    e.preventDefault();
    handleValidateCodeClient();
  }, [handleValidateCodeClient]);

  return {
    codeClient,
    setCodeClient,
    isCodeValidated,
    setIsCodeValidated,
    codeValidationError,
    setCodeValidationError,
    isCheckingCode,
    handleCodeFormSubmit,
    handleValidateCodeClient
  };
}
